/**
 * Constants for guest access system to maintain DRY principles
 */
import type { DurationKey, DurationOption } from '@/components/common/access-time-selector/types.ts';
import { ACCESS_TIME_SLOT_MINUTES } from '@/utils/access/timeSlots';

// Access type modes
export const ACCESS_TYPE_TEMPORARY = 'temporary';
export const ACCESS_TYPE_INDEFINITE = 'indefinite';

// Duration unit types
export type DurationUnit = 'hours' | 'days' | 'weeks' | 'months' | 'years';

// Duration preset type
export interface DurationPreset {
	label: string;
	value: number;
	unit: DurationUnit;
}

export const MORE_OPTIONS_BASE: DurationOption[] = [
	{ value: 'today', label: 'Today' },
	{ value: 'tomorrow', label: 'Tomorrow' },
	{ value: '1w', label: '7 days' },
	{ value: '30d', label: '30 days' },
	{ value: '90d', label: '90 days' },
	{ value: '1y', label: '1 year' },
];

export const PRESET_MAP: Record<Exclude<DurationKey, 'indefinite' | 'custom'>, DurationPreset> = {
	today: { label: 'Today', value: 0, unit: 'days' },
	tomorrow: { label: 'Tomorrow', value: 1, unit: 'days' },
	'4h': { label: '4 hours', value: 4, unit: 'hours' },
	'8h': { label: '8 hours', value: 8, unit: 'hours' },
	'12h': { label: '12 hours', value: 12, unit: 'hours' },
	'1d': { label: '1 day', value: 1, unit: 'days' },
	'2d': { label: '2 days', value: 2, unit: 'days' },
	'3d': { label: '3 days', value: 3, unit: 'days' },
	'4d': { label: '4 days', value: 4, unit: 'days' },
	'5d': { label: '5 days', value: 5, unit: 'days' },
	'6d': { label: '6 days', value: 6, unit: 'days' },
	'1w': { label: '1 week', value: 1, unit: 'weeks' },
	'30d': { label: '30 days', value: 30, unit: 'days' },
	'2w': { label: '2 weeks', value: 2, unit: 'weeks' },
	'3w': { label: '3 weeks', value: 3, unit: 'weeks' },
	'1mo': { label: '1 month', value: 1, unit: 'months' },
	'2mo': { label: '2 months', value: 2, unit: 'months' },
	'3mo': { label: '3 months', value: 3, unit: 'months' },
	'4mo': { label: '4 months', value: 4, unit: 'months' },
	'5mo': { label: '5 months', value: 5, unit: 'months' },
	'6mo': { label: '6 months', value: 6, unit: 'months' },
	'90d': { label: '90 days', value: 90, unit: 'days' },
	'1y': { label: '1 year', value: 1, unit: 'years' },
};

export const TIME_INCREMENTS: string[] = [];
for (let h = 0; h < 24; h++) {
	for (let m = 0; m < 60; m += ACCESS_TIME_SLOT_MINUTES) {
		TIME_INCREMENTS.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
	}
}

// Status messages
export const STATUS_MESSAGES = {
	INITIALIZING: 'Initializing access request...',
	WAITING: 'Waiting for approval...',
	FAILED: 'Access request failed. Redirecting to login...',
	SUCCESS: 'Access granted successfully',
} as const;

// Route constants
export const ROUTES = {
	DASHBOARD: '/dashboard',
	LOGIN: '/login',
} as const;

// Default values
export const DEFAULT_ACCESS_TYPE = ACCESS_TYPE_TEMPORARY;
export const DEFAULT_PURPOSE = 'Property Access';

/**
 * Personal note attached to a sharing invite (including renewals through SharePopover), included in the
 * invite email — distinct from access_purpose/DEFAULT_PURPOSE above, which is the
 * guest-request-reason field.
 */
export const INVITE_MESSAGE_LABEL = 'Message (optional)';
export const INVITE_MESSAGE_PLACEHOLDER = "e.g. Hi, here's access to Bundle for our property.";

/**
 * Format requested access duration text for display in dialogs
 * Handles both ISO date strings and null values (indefinite access)
 */
export const formatRequestedAccessDuration = (desiredExpiresAt: string | null): string => {
	if (!desiredExpiresAt) {
		return 'Access has been requested indefinitely (until manually removed)';
	}

	try {
		const expirationDate = new Date(desiredExpiresAt);
		const formattedDate = new Intl.DateTimeFormat(undefined, {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
		}).format(expirationDate);
		return `Access has been requested until ${formattedDate}`;
	} catch {
		// Fallback for invalid date strings
		return 'Access has been requested with an invalid expiration date';
	}
};
